import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { MOBILE_BREAKPOINT } from "../../config";
import { GetBookingQuery } from "../../graphql/generated";
import styled from "../../styles";
import { Chevron } from "../Chevron";
import { CopyTextButton } from "../CopyTextButton";
import { Flex } from "../Flex";
import { Text } from "../Text";
import { View } from "../View";
import { MailIcon } from "../icons/MailIcon";
import { NameIcon } from "../icons/NameIcon";
import { PhoneIcon } from "../icons/PhoneIcon";

const BioWrap = styled(View)`
  width: 100%;
  max-width: 500px;
`;

export const StackOnMobile = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${(p) => p.theme.spacing.m};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
    flex-direction: column;
    gap: ${(p) => p.theme.spacing.xxs};
  }
`;

const MotionFlex = styled(motion(Flex))`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding-bottom: ${(p) => p.theme.spacing.m};
  }
`;

const MotionView = styled(motion(View))`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin: 0;
    div {
      margin: ${(p) => p.theme.spacing.xs} 0;
    }
  }
`;

export const ContactDetails = ({
  booking,
  collapsable = true,
}: {
  booking: GetBookingQuery["booking"];
  collapsable?: boolean;
}) => {
  const [showContactDetails, setShowContactDetails] = useState(
    collapsable ? false : true
  );

  if (!booking.creator.preferredProfile) {
    return null;
  }

  if (booking.creator.accounts.length === 0) {
    return null;
  }

  const expandVariants = {
    collapsed: {
      height: 0,
      opacity: 0,
      transition: {
        height: { duration: 0.15, ease: [0.4, 0, 1, 1] },
        opacity: { duration: 0.1, ease: "easeOut" },
      },
    },
    expanded: {
      height: "auto",
      opacity: 1,
      transition: {
        height: { duration: 0.2, ease: [0, 0, 0.2, 1] },
        opacity: { duration: 0.15, ease: "easeOut" },
      },
    },
  };

  return (
    <>
      {collapsable && (
        <StackOnMobile>
          <BioWrap>
            <Text margin="s 0 s">
              {booking.creator.preferredProfile.biography}
            </Text>
          </BioWrap>
          <StyledFlex
            margin="s 0 0 0"
            align="center"
            style={{ cursor: "pointer", userSelect: "none" }}
            onClick={() => setShowContactDetails(!showContactDetails)}
            whileHover={{ filter: "brightness(0.8)" }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.08, ease: "easeOut" }}
          >
            <Text weight="semi" colorPreset="link" margin="0 s 0 0">
              Contact Details
            </Text>
            <Chevron
              size="s"
              direction={showContactDetails ? "up" : "down"}
              colorPreset="link"
              animateTransition
            />
          </StyledFlex>
        </StackOnMobile>
      )}
      <AnimatePresence initial={false}>
        {showContactDetails && (
          <MotionFlex
            align="center"
            margin="0"
            initial="collapsed"
            animate="expanded"
            exit="collapsed"
            variants={expandVariants as any}
            style={{ overflow: "visible" }}
          >
            <MotionView
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
              variants={{
                collapsed: { opacity: 0 },
                expanded: {
                  opacity: 1,
                  transition: {
                    duration: 0.12,
                    ease: "easeOut",
                  },
                },
              }}
            >
              <StackOnMobile>
                {booking.creator.accounts[0] &&
                booking.creator.accounts[0].firstName ? (
                  <CopyTextButton
                    text={`${booking.creator.accounts[0].firstName}${
                      booking.creator.accounts[0].lastName
                        ? ` ${booking.creator.accounts[0].lastName}`
                        : null
                    }`}
                    icon={<NameIcon colorPreset="text" />}
                  />
                ) : null}
                {booking.creator.accounts[0] &&
                booking.creator.accounts[0].email ? (
                  <CopyTextButton
                    text={booking.creator.accounts[0].email}
                    icon={
                      <MailIcon width={16} height={12} colorPreset="text" />
                    }
                  />
                ) : null}
                {booking.creator.accounts[0] &&
                booking.creator.accounts[0].phoneNumber ? (
                  <CopyTextButton
                    text={booking.creator.accounts[0].phoneNumber}
                    icon={
                      <PhoneIcon width={12} height={12} colorPreset="text" />
                    }
                  />
                ) : null}
              </StackOnMobile>
            </MotionView>
          </MotionFlex>
        )}
      </AnimatePresence>
    </>
  );
};

const StyledFlex = styled(motion(Flex))`
  :hover {
    filter: brightness(0.8);
  }
`;
